import {
    BillingPlatform,
    DependenciesPayload,
    EligibilityOffer,
} from '@sxm-de/dev-kits/web-sdk/types';
import { MapClientSdkToWebSdk } from '@sxm-de/dev-kits/web-sdk/utils';

export interface ProductGroupView {
    name: string;
    id: string;
    products: { id: string }[];
}

interface OfferDetailsResponse {
    offer: EligibilityOffer;
    campaignId?: string | null;
    productGroups: ProductGroupView[];
}

export async function getOfferDetailsCall(
    { offerId }: { offerId: string },
    { clientSdk }: DependenciesPayload,
): Promise<OfferDetailsResponse> {
    const mapClientSdkToWebSdk: MapClientSdkToWebSdk<
        Awaited<
            ReturnType<typeof clientSdk.commerce.eligibility.getOfferDetails>
        >,
        OfferDetailsResponse
    > = ({ offer, campaignId, productGroups }) => {
        let billingPlatform: BillingPlatform = 'Direct';
        if ('amazonAppStore' in offer.billingPlatform) {
            billingPlatform = 'AmazonAppStore';
        } else if ('appleAppStore' in offer.billingPlatform) {
            billingPlatform = 'AppleAppStore';
        } else if ('googlePlayStore' in offer.billingPlatform) {
            billingPlatform = 'GooglePlayStore';
        } else if ('rokuPay' in offer.billingPlatform) {
            billingPlatform = 'RokuPay';
        }
        return {
            offer: {
                ...offer,
                name: offer.name || '',
                products: offer.products.map((product) => {
                    return {
                        ...product,
                        productType: (product as any).productType as Record<
                            'partner' | 'main',
                            unknown
                        >,
                    };
                }),
                billingPlatform,
                basePrice: {
                    productAmount: {
                        value: offer.basePrice?.productAmount.value || 0,
                    },
                    total: {
                        value: offer.basePrice?.total.value || 0,
                    },
                },
                phases:
                    offer.phases?.map((phase) => {
                        return {
                            ...phase,
                            duration: {
                                finite:
                                    'finite' in phase.duration
                                        ? phase.duration.finite
                                        : {
                                              length: 0,
                                              timeUnit: 'Months',
                                          },
                                infinite:
                                    'infinite' in phase.duration
                                        ? phase.duration.infinite
                                        : {},
                            },
                            billingPeriod: phase.billingPeriod || {
                                length: 0,
                                timeUnit: 'Months',
                            },
                            price: phase.price
                                ? {
                                      ...phase.price,
                                      mrfAmount: phase.price.mrfAmount || {
                                          value: 0,
                                      },
                                      calculatedAs: {
                                          basePrice: 0,
                                          fixedDiscount: 0,
                                          percentageDiscount: 0,
                                      },
                                  }
                                : {
                                      productAmount: { value: 0 },
                                      total: { value: 0 },
                                      mrfAmount: { value: 0 },
                                      calculatedAs: {
                                          basePrice: 0,
                                          fixedDiscount: 0,
                                          percentageDiscount: 0,
                                      },
                                  },
                        };
                    }) || [],
            },
            campaignId,
            productGroups,
        };
    };

    const offerDetails = mapClientSdkToWebSdk(
        await clientSdk.commerce.eligibility.getOfferDetails(offerId),
    );
    return offerDetails;
}
